/**
 * Painel de exportação de transcrições
 */

import React, { useState, useCallback } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  Stack,
  Typography
} from '@mui/material'
import DescriptionIcon from '@mui/icons-material/Description'
import CodeIcon from '@mui/icons-material/Code'
import ArticleIcon from '@mui/icons-material/Article'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import DownloadIcon from '@mui/icons-material/Download'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { Transcription, AISummary, UserAnnotations } from '../../../types/transcription'
import {
  downloadAsText,
  downloadAsHTML,
  downloadAsDocx,
  exportToHTML,
  SpeakerMapping
} from '../../../services/exportService'
import { useNPS } from '../../../contexts/NPSContext'

type ExportFormat = 'txt' | 'html' | 'docx' | 'pdf'

type Props = {
  transcription: Transcription
  aiSummary?: AISummary | null
  speakerMapping?: SpeakerMapping
  annotations?: UserAnnotations
}

const EXPORT_OPTIONS: { format: ExportFormat; label: string; description: string; icon: React.ReactNode }[] = [
  {
    format: 'txt',
    label: 'Texto Simples (.txt)',
    description: 'Formato básico, compatível com qualquer editor',
    icon: <DescriptionIcon sx={{ color: '#455a64' }} />
  },
  {
    format: 'html',
    label: 'HTML (.html)',
    description: 'Documento formatado para navegador',
    icon: <CodeIcon sx={{ color: '#e65100' }} />
  },
  {
    format: 'docx',
    label: 'Word (.docx)',
    description: 'Documento editável do Microsoft Word',
    icon: <ArticleIcon sx={{ color: '#2e7d32' }} />
  },
  {
    format: 'pdf',
    label: 'PDF (.pdf)',
    description: 'Documento profissional para impressão',
    icon: <PictureAsPdfIcon sx={{ color: '#c62828' }} />
  }
]

export default function ExportPanel({ transcription, aiSummary, speakerMapping = {}, annotations }: Props) {
  const [includeSummary, setIncludeSummary] = useState(true)
  const [includeAnnotations, setIncludeAnnotations] = useState(true)
  const [exporting, setExporting] = useState<ExportFormat | null>(null)
  const { triggerNPS } = useNPS()

  const hasMappings = Object.keys(speakerMapping).length > 0
  const hasAnnotationsData = annotations && (annotations.items.length > 0 || annotations.highlights.length > 0 || annotations.bookmarks.length > 0)
  const totalAnnotations = annotations ? annotations.items.length + annotations.highlights.length + annotations.bookmarks.length : 0

  const handleExport = useCallback(async (format: ExportFormat) => {
    setExporting(format)

    try {
      const options = {
        includeSummary: includeSummary && aiSummary ? aiSummary : undefined,
        speakerMapping: hasMappings ? speakerMapping : undefined,
        annotations: includeAnnotations && hasAnnotationsData ? annotations : undefined
      }

      switch (format) {
        case 'txt':
          downloadAsText(transcription, options)
          break
        case 'html':
          downloadAsHTML(transcription, options)
          break
        case 'docx':
          await downloadAsDocx(transcription, options)
          break
        case 'pdf': {
          // PDF: gera o HTML como Blob, abre em nova aba e dispara impressão
          // window.open deve ser chamado ANTES de qualquer await para não ser bloqueado como popup
          const htmlContent = exportToHTML(transcription, options)
          const pdfBlob = new Blob([htmlContent], { type: 'text/html;charset=utf-8' })
          const pdfUrl = URL.createObjectURL(pdfBlob)
          const printWindow = window.open(pdfUrl, '_blank')
          if (printWindow) {
            printWindow.addEventListener('load', () => {
              printWindow.focus()
              printWindow.print()
            })
            setTimeout(() => URL.revokeObjectURL(pdfUrl), 60000)
          } else {
            // Fallback quando popup é bloqueado: faz download do HTML
            // O usuário pode abrir o arquivo e imprimir como PDF pelo navegador
            const { downloadAsHTML: dlHTML } = await import('../../../services/exportService')
            dlHTML(transcription, options)
          }
          break
        }
      }

      // Dispara NPS após exportação bem-sucedida
      triggerNPS('EXPORT')
    } finally {
      setTimeout(() => setExporting(null), 500)
    }
  }, [transcription, aiSummary, speakerMapping, annotations, includeSummary, includeAnnotations, hasMappings, hasAnnotationsData, triggerNPS])

  return (
    <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          📥 Exportar Transcrição
        </Typography>

        <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>
          Baixe a transcrição no formato desejado para uso offline ou compartilhamento.
        </Typography>

        {/* Indicador de nomes personalizados */}
        {hasMappings && (
          <Box sx={{ mb: 3, p: 2, bgcolor: '#e8f5e9', borderRadius: 2, border: '1px solid #a5d6a7' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircleIcon sx={{ color: '#4caf50' }} />
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                  Nomes personalizados aplicados
                </Typography>
                <Typography variant="caption" sx={{ color: '#666' }}>
                  {Object.keys(speakerMapping).length} participante(s) com nomes definidos na aba Transcrição
                </Typography>
              </Box>
            </Box>
            <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {Object.entries(speakerMapping).map(([original, mapped]) => (
                <Chip
                  key={original}
                  label={`${original} → ${mapped}`}
                  size="small"
                  variant="outlined"
                  sx={{ bgcolor: '#fff' }}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Opção de incluir resumo */}
        {aiSummary && (
          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeSummary}
                  onChange={(e) => setIncludeSummary(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body2">Incluir resumo da IA</Typography>
                  <Typography variant="caption" sx={{ color: '#666' }}>
                    Adiciona o resumo, pontos-chave e ações pendentes ao documento
                  </Typography>
                </Box>
              }
            />
          </Box>
        )}

        {/* Opção de incluir anotações */}
        {hasAnnotationsData && (
          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeAnnotations}
                  onChange={(e) => setIncludeAnnotations(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body2">
                    Incluir anotações
                    <Chip
                      label={`${totalAnnotations}`}
                      size="small"
                      sx={{ ml: 1, bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 600, height: 20, fontSize: '0.75rem' }}
                    />
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#666' }}>
                    Adiciona notas ({annotations!.items.length}), destaques ({annotations!.highlights.length}) e favoritos ({annotations!.bookmarks.length}) ao documento
                  </Typography>
                </Box>
              }
            />
          </Box>
        )}

        <Divider sx={{ mb: 3 }} />

        {/* Opções de formato */}
        <Grid container spacing={2}>
          {EXPORT_OPTIONS.map((option) => (
            <Grid item xs={12} sm={6} key={option.format}>
              <Box
                sx={{
                  p: 2,
                  border: '1px solid #e0e0e0',
                  borderRadius: 2,
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: '#1976d2',
                    bgcolor: '#f5f5f5'
                  }
                }}
              >
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      bgcolor: '#f5f5f5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {option.icon}
                  </Box>

                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {option.label}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 1 }}>
                      {option.description}
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      onClick={() => handleExport(option.format)}
                      disabled={exporting !== null}
                      sx={{ textTransform: 'none' }}
                    >
                      {exporting === option.format ? 'Exportando...' : 'Baixar'}
                    </Button>
                  </Box>
                </Stack>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Informações adicionais */}
        <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
          <Typography variant="caption" sx={{ color: '#666' }}>
            💡 <strong>Dica:</strong> Use o formato Word (.docx) para editar o documento posteriormente.
            O formato PDF é ideal para compartilhar documentos finalizados.
            {!hasMappings && (
              <span style={{ display: 'block', marginTop: 8 }}>
                ℹ️ Para personalizar os nomes dos participantes, acesse a aba <strong>Transcrição</strong>.
              </span>
            )}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  )
}
